import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import ToastHelper from "@/utils/toastHelper";
import { Button } from "@/components/ui/button";
import { adminEnrolmentService } from "@/services/adminEnrolmentService";
import EnrollmentWorkflowTimeline from "@/components/EnrollmentWorkflowTimeline";
import { certificateService } from "@/services/certificateService";
import { enrollmentDocumentService } from "@/services/enrollmentDocumentService";
import { gridExportService } from "@/services/gridExportService";
import OnlineEnrolmentNew from "@/pages/student/OnlineEnrolmentNew";
import {
  canDownloadEnrollmentDocument,
  EnrollmentActionStatusMap,
  EnrollmentStatus,
  EnrollmentStatusMeta,
  getStatusMeta,
} from "@/utils/enrolmentStatus";
import StudentEnrollmentDocuments from "@/pages/student/enrollment/StudentEnrollmentDocuments";

const DEFAULT_STEP_OPTION = { value: "", label: "All steps" };
const DEFAULT_WORKFLOW_STATUS_OPTION = { value: "", label: "All statuses" };

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBoolean = (value) => {
  if (value === true) {
    return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />Yes</span>;
  }

  if (value === false) {
    return <span className="inline-flex items-center gap-1 text-slate-500"><XCircle className="h-4 w-4" />No</span>;
  }

  return "-";
};

const readPerm = (source, camelName, pascalName) => source?.[camelName] ?? source?.[pascalName];

const normalizeAction = (action) => typeof action === "string"
  ? action
  : action?.text ?? action?.Text ?? action?.action ?? action?.name ?? action?.label ?? action?.Action ?? action?.Name ?? action?.Label ?? "";

const getButtonNextStatus = (button) => {
  const nextStatus = Number(button?.nextWorkFlowStatusNo ?? button?.NextWorkFlowStatusNo);
  if (Number.isFinite(nextStatus) && nextStatus > 0) return nextStatus;
  const action = normalizeAction(button);
  return EnrollmentActionStatusMap[action] || 0;
};

const readWorkflowValue = (source, camelName, pascalName, fallback = 0) => {
  const value = source?.[camelName] ?? source?.[pascalName];
  return value ?? fallback;
};

const buildEnrollmentActionPayload = (enrolment, button, action, nextStatus, adminComments) => {
  const currentWorkFlowStatus = enrolment?.currentWorkFlowStatus || enrolment?.CurrentWorkFlowStatus || {};
  const currentStatusNo = Number(
    readWorkflowValue(currentWorkFlowStatus, "workFlowStatusNo", "WorkFlowStatusNo",
      enrolment?.workFlowStatusNo ?? enrolment?.WorkFlowStatusNo ?? button?.currentWorkFlowStatusNo ?? button?.CurrentWorkFlowStatusNo ?? 0)
  ) || 0;
  const currentStatusName = readWorkflowValue(currentWorkFlowStatus, "workFlowStatusName", "WorkFlowStatusName",
    enrolment?.workFlowStatusName ?? enrolment?.WorkFlowStatusName ?? "");

  return {
    ...enrolment,
    studentEnrollmentId: enrolment.studentEnrollmentId,
    action,
    adminComments,
    rejectedReason: action === "Reject" ? adminComments : "",
    currentWorkFlowStatus: {
      ...currentWorkFlowStatus,
      workFlowStatusNo: currentStatusNo,
      workFlowStatusName: currentStatusName,
      nextWorkFlowStatusNo: nextStatus,
    },
  };
};

const isEmptyDate = (date) => Number.isNaN(date.getTime()) || date.getFullYear() <= 1;
const dateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isEmptyDate(date)) return "";
  return String(value).slice(0, 16);
};
const boolOrNull = (value) => value === true || Number(value) === 1 ? true : value === false || Number(value) === 0 ? false : null;
function StaffYesNoBoxes({ value, disabled, onChange }) {
  return (
    <div className="flex min-w-32 items-center gap-5">
      <label className="inline-flex items-center gap-1.5"><input type="checkbox" className="h-4 w-4 rounded border-slate-400" disabled={disabled} checked={value === true} onChange={() => onChange(true)} /> Yes</label>
      <label className="inline-flex items-center gap-1.5"><input type="checkbox" className="h-4 w-4 rounded border-slate-400" disabled={disabled} checked={value === false} onChange={() => onChange(false)} /> No</label>
    </div>
  );
}

const normalizeEnrolments = (list) => (Array.isArray(list) ? list : []).map((item) => ({
    id: item.studentEnrollmentId ?? item.studentEnrollmentID ?? item.id,
    studentEnrollmentId: item.studentEnrollmentId ?? item.studentEnrollmentID ?? item.id,
    studentBookingId: item.studentBookingId ?? item.studentBookingID,
    studentId: item.studentId ?? item.studentID,
    bookingNumber: item.bookingNumber ?? item.bookingNo ?? "-",
    studentName: item.studentName ?? "Unknown",
    email: item.email ?? "-",
    mobileNumber: item.mobileNumber ?? item.mobile ?? "-",
    courseName: item.courseName ?? "-",
    campusName: item.campusName ?? "-",
    scheduleDate: formatDate(item.scheduleDate),
    submittedDate: formatDate(item.submittedDate),
    progressPercentage: item.progressPercentage ?? 0,
    studentProgressPercentage: item.studentProgressPercentage ?? 0,
    instituteProgressPercentage: item.instituteProgressPercentage ?? 0,
    currentStep: item.currentStep ?? "-",
    usiNumber: item.usiNumber ?? "-",
    usiVerified: item.usiVerified ?? false,
    documentsVerified: item.documentsVerified ?? false,
    workFlowStatusNo: item.workFlowStatusNo,
    workFlowStatusName: item.workFlowStatusName ?? getStatusMeta(item.workFlowStatusNo).label,
  }));

const normalizeOptions = (options, fallback) => [
  fallback,
  ...(Array.isArray(options) ? options.map((item) => ({
    value: String(item.value ?? item.id ?? ""),
    label: item.label ?? item.name ?? String(item.value ?? item.id ?? ""),
  })) : []),
];

const ProgressIndicator = ({ percentage, step }) => {
  const value = Math.max(0, Math.min(100, Number(percentage) || 0));
  return <div className="min-w-32"><div className="mb-1 flex justify-between text-xs"><span className="font-semibold text-slate-700">{value}%</span><span className="max-w-20 truncate text-slate-500" title={step}>{step || "Not started"}</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${value === 100 ? "bg-emerald-500" : "bg-orange-500"}`} style={{ width: `${value}%` }} /></div></div>;
};

export default function AdminEnrolments() {
  const navigate = useNavigate();
  const [enrolments, setEnrolments] = useState([]);
  const [filters, setFilters] = useState({ courseStepTypeNo: "", workFlowStatusNo: "", attentionFilter: "", searchText: "", page: 1, pageSize: 10 });
  const [stepOptions, setStepOptions] = useState([DEFAULT_STEP_OPTION]);
  const [workflowStatusOptions, setWorkflowStatusOptions] = useState([DEFAULT_WORKFLOW_STATUS_OPTION]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [selectedEnrolments, setSelectedEnrolments] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [certificateDialog, setCertificateDialog] = useState(null);
  const [historyEnrolment, setHistoryEnrolment] = useState(null);

  const viewEnrolment = async (enrolment) => {
    setDetailLoading(true);
    try {
      const payload = await adminEnrolmentService.getEnrolmentDetail(enrolment.studentBookingId);
      setDetail({
        summary: enrolment,
        enrollment:
          payload?.enrollment ||
          payload?.studentEnrollment ||
          payload?.Enrollment ||
          payload?.StudentEnrollment ||
          payload,
        booking: payload?.booking || payload?.Booking || {},
        documents:
          payload?.documents ||
          payload?.studentDocuments ||
          payload?.Documents ||
          payload?.StudentDocuments ||
          [],
        settingPerm:
          payload?.settingPerm ||
          payload?.enrollment?.settingPerm ||
          payload?.studentEnrollment?.settingPerm ||
          payload?.Enrollment?.SettingPerm ||
          payload?.StudentEnrollment?.SettingPerm ||
          {},
        buttonList:
          payload?.buttonList ||
          payload?.ButtonList ||
          [],
        canViewSummary: payload?.canViewSummary ?? payload?.CanViewSummary,
        masterData:
          payload?.masterData ||
          payload?.MasterData ||
          {},
      });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to load enrolment details");
    } finally { setDetailLoading(false); }
  };

  const fetchEnrolments = useCallback(async () => {
    setLoading(true);

    try {
      const response = await adminEnrolmentService.getAllEnrolments({
        courseStepTypeNo: filters.courseStepTypeNo || undefined,
        workFlowStatusNo: filters.workFlowStatusNo || undefined,
        attentionFilter: filters.attentionFilter || undefined,
        searchText: filters.searchText || undefined,
        page: filters.page,
        pageSize: filters.pageSize,
      });
      const nextEnrolments = normalizeEnrolments(response?.enrolments ?? response?.data ?? response?.result ?? response?.items ?? []);
      setEnrolments(nextEnrolments);
      setStepOptions(normalizeOptions(response?.stepOptions, DEFAULT_STEP_OPTION));
      setWorkflowStatusOptions(normalizeOptions(response?.workflowStatusOptions, DEFAULT_WORKFLOW_STATUS_OPTION));
      setTotalRecords(Number(response?.totalRecords || 0));
      setTotalPages(Math.max(1, Number(response?.totalPages || 1)));
      setSelectedEnrolments([]);
    } catch (error) {
      console.error("Failed to load enrolments", error);
      setEnrolments([]);
      setTotalRecords(0);
      setTotalPages(1);
      setSelectedEnrolments([]);
      ToastHelper.apiError(error, "Unable to load enrolments");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const toggleSelection = (enrolment) => {
    setSelectedEnrolments((current) => {
      const id = enrolment.studentEnrollmentId ?? enrolment.id;
      const exists = current.some((item) => item.enrolmentId === id);

      if (exists) {
        return current.filter((item) => item.enrolmentId !== id);
      }

      return [...current, { enrolmentId: id, studentName: enrolment.studentName }];
    });
  };

  const toggleSelectAll = () => {
    if (selectedEnrolments.length === enrolments.length) {
      setSelectedEnrolments([]);
      return;
    }

    setSelectedEnrolments(
      enrolments.map((enrolment) => ({
        enrolmentId: enrolment.studentEnrollmentId ?? enrolment.id,
        studentName: enrolment.studentName,
      }))
    );
  };

  const handleAction = async (enrolment, button, adminComments = "", onDone) => {
    if (!enrolment?.studentEnrollmentId) {
      ToastHelper.error("Unable to process this enrolment because the ID is missing.");
      return;
    }
    const action = normalizeAction(button);
    const nextStatus = getButtonNextStatus(button);
    if (!action || !nextStatus) {
      ToastHelper.error("This workflow action is not available.");
      return;
    }

    setProcessingId(enrolment.studentEnrollmentId);

    try {
      const payload = buildEnrollmentActionPayload(enrolment, button, action, nextStatus, adminComments);

      await adminEnrolmentService.performEnrolmentAction(payload);
      ToastHelper.success(`${action} action completed for ${enrolment.studentName}`);
      await fetchEnrolments();
      if (typeof onDone === "function") await onDone();
      setActionDialog(null);
    } catch (error) {
      console.error("Failed to update enrolment", error);
      ToastHelper.apiError(error, `Unable to ${action.toLowerCase()} enrolment`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleExportCsvAsqa = async () => {
    try {
      const response = await adminEnrolmentService.exportCsvAsqa({
        selectedRecords: selectedEnrolments.length > 0
          ? selectedEnrolments.map((item) => ({
              studentEnrollmentId: item.enrolmentId,
              studentId: item.studentId ?? 0,
            }))
          : [
              {
                studentEnrollmentId: 0,
                studentId: 0,
              },
            ],
        status: currentWorkflowStatusLabel,
      });

      const disposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"] || "";
      const now = new Date();
      const dateTime = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-") + `_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
      const fileName = disposition.split("filename=")[1]?.replace(/"/g, "") || `enrolments-asqa-${dateTime}.csv`;
      const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      ToastHelper.success("ASQA CSV export downloaded");
    } catch (error) {
      console.error("Failed to export ASQA CSV", error);
      if (error?.response?.data instanceof Blob) {
        try {
          const validation = JSON.parse(await error.response.data.text());
          ToastHelper.error(validation.message || validation.errors?.[0]?.message || "Unable to export ASQA CSV");
          return;
        } catch {
        }
      }
      ToastHelper.apiError(error, "Unable to export ASQA CSV");
    }
  };

  useEffect(() => {
    fetchEnrolments();
  }, [fetchEnrolments]);

  const summaryText = useMemo(() => {
    if (loading) return "Loading enrolments...";
    const from = totalRecords ? ((filters.page - 1) * filters.pageSize) + 1 : 0;
    const to = Math.min(filters.page * filters.pageSize, totalRecords);
    return `Showing ${from}-${to} of ${totalRecords}`;
  }, [filters.page, filters.pageSize, loading, totalRecords]);

  const overview = useMemo(() => ({
    review: enrolments.filter((item) => [EnrollmentStatus.Submitted, EnrollmentStatus.Returned, EnrollmentStatus.DocumentsPending, EnrollmentStatus.PaymentReceived].includes(Number(item.workFlowStatusNo))).length,
    approved: enrolments.filter((item) => Number(item.workFlowStatusNo) === EnrollmentStatus.Approved).length,
    completed: enrolments.filter((item) => [EnrollmentStatus.Completed, EnrollmentStatus.CertificateIssued].includes(Number(item.workFlowStatusNo))).length,
  }), [enrolments]);

  const updateFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value, page: name === "page" ? value : 1 }));
  const updateCourseStepFilter = (value) => setFilters((current) => ({
    ...current,
    courseStepTypeNo: value,
    workFlowStatusNo: "",
    page: 1,
  }));
  const resetFilters = () => setFilters({ courseStepTypeNo: "", workFlowStatusNo: "", attentionFilter: "", searchText: "", page: 1, pageSize: 10 });
  const applyAttentionFilter = (value) => setFilters((current) => ({ ...current, attentionFilter: current.attentionFilter === value ? "" : value, page: 1 }));
  const currentWorkflowStatusLabel = workflowStatusOptions.find((item) => item.value === String(filters.workFlowStatusNo))?.label || "All";

  const enrolmentExportPayload = {
    title: "Enrolments",
    columns: ["ID", "Student", "Email", "Mobile", "Booking", "Course", "Campus", "Schedule", "Student progress", "Institute progress", "Current step", "Workflow status"],
    rows: enrolments.map((item) => [
      `E-${item.studentEnrollmentId}`,
      item.studentName,
      item.email,
      item.mobileNumber,
      item.bookingNumber,
      item.courseName,
      item.campusName,
      item.scheduleDate,
      `${item.studentProgressPercentage}%`,
      `${item.instituteProgressPercentage}%`,
      item.currentStep,
      item.workFlowStatusName,
    ].map((value) => String(value ?? ""))),
  };

  const exportEnrolments = async (format) => {
    try {
      await (format === "excel" ? gridExportService.exportExcel(enrolmentExportPayload) : gridExportService.exportPdf(enrolmentExportPayload));
      ToastHelper.success(`Enrolments ${format === "excel" ? "Excel" : "PDF"} downloaded`);
    } catch (error) {
      ToastHelper.apiError(error, `Unable to export enrolments to ${format === "excel" ? "Excel" : "PDF"}`);
    }
  };

  const canSelectAll = enrolments.length > 0 && selectedEnrolments.length === enrolments.length;

  const filterSummary = filters.courseStepTypeNo || filters.workFlowStatusNo || filters.attentionFilter || filters.searchText
    ? "Server-side filters are active."
    : "All enrolments are shown with server-side paging.";

  return (
    <div className="space-y-5" data-testid="enrolments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Enrolments</h1>
          <div className="text-sm text-slate-500">Review and manage enrolment progression across all statuses.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleExportCsvAsqa} className="wc-btn-outline text-xs" data-testid="enrolments-export-csv">Export to CSV (ASQA)</button>
          <button onClick={() => exportEnrolments("excel")} className="wc-btn-outline text-xs" data-testid="enrolments-export-excel">Export Excel</button>
          <button onClick={() => exportEnrolments("pdf")} className="wc-btn-outline text-xs" data-testid="enrolments-export-pdf">Export PDF</button>
          <button onClick={() => ToastHelper.success("RTO export prepared")} className="wc-btn-outline text-xs" data-testid="enrolments-export-rto">Export to RTO</button>
          <div className="text-sm font-medium text-slate-600">{summaryText}</div>
        </div>
      </div>

      <div className="wc-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enrolment filters</div>
            <div className="text-xs text-slate-500">Choose any step and workflow status from the dropdowns. {filterSummary}</div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={fetchEnrolments} disabled={loading}>
            Refresh
          </Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="xl:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Search</span>
            <input className="wc-input" placeholder="Student, email, mobile, booking, course, campus..." value={filters.searchText} onChange={(event) => updateFilter("searchText", event.target.value)} />
          </label>
          <FilterSelect label="Course step" value={filters.courseStepTypeNo} options={stepOptions} onChange={updateCourseStepFilter} />
          <FilterSelect label="Workflow status" value={filters.workFlowStatusNo} options={workflowStatusOptions} onChange={(value) => updateFilter("workFlowStatusNo", value)} disabled={!filters.courseStepTypeNo} />
          <div className="flex items-end">
            <button className="wc-btn-outline w-full" onClick={resetFilters}>Clear filters</button>
          </div>
        </div>
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Quick filters</div>
          <div className="flex flex-wrap gap-2">
            {[
              { value: "admin", label: "Pending Admin", activeClass: "border-amber-600 bg-amber-600 text-white" },
              { value: "student", label: "Pending Student", activeClass: "border-blue-600 bg-blue-600 text-white" },
              { value: "completed", label: "Completed", activeClass: "border-emerald-600 bg-emerald-600 text-white" },
              { value: "certificate", label: "Pending Certificate Issue", activeClass: "border-violet-600 bg-violet-600 text-white" },
            ].map((option) => {
              const active = filters.attentionFilter === option.value;
              return <button key={option.value} type="button" aria-pressed={active} onClick={() => applyAttentionFilter(option.value)} className={`inline-flex h-10 items-center rounded-lg border px-4 text-sm font-bold transition ${active ? option.activeClass : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>{option.label}</button>;
            })}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[{ label: "Total enrolments", value: totalRecords, style: "text-[#0A2540]" }, { label: "Needs review", value: overview.review, style: "text-amber-700" }, { label: "Approved", value: overview.approved, style: "text-emerald-700" }, { label: "Completed / certified", value: overview.completed, style: "text-teal-700" }].map((item) => <div key={item.label} className="wc-card py-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</div><div className={`mt-1 text-2xl font-display font-extrabold ${item.style}`}>{item.value}</div></div>)}
      </div>

      <div className="wc-card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading enrolments...
          </div>
        ) : enrolments.length === 0 ? (
          <div className="px-4 py-16 text-center text-slate-500">
            {filters.attentionFilter === "certificate"
              ? "No enrolments are waiting for certificate issuance. Certificates have already been issued for all completed enrolments."
              : "No enrolments found for this view."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={canSelectAll}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left">Booking Number</th>
                  <th className="px-4 py-3 text-left">Student Name</th>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Campus</th>
                  <th className="px-4 py-3 text-left">Schedule Date</th>
                  <th className="px-4 py-3 text-left">Student Progress</th>
                  <th className="px-4 py-3 text-left">Institute Progress</th>
                  <th className="px-4 py-3 text-left">Compliance checks</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enrolments.map((enrolment) => {
                  const enrolmentId = enrolment.studentEnrollmentId ?? enrolment.id;
                  const isSelected = selectedEnrolments.some((item) => item.enrolmentId === enrolmentId);

                  return (
                    <tr key={enrolment.id} className="hover:bg-slate-50 align-top">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(enrolment)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                      </td>
                    <td className="px-4 py-3">{enrolment.bookingNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{enrolment.studentName}</div>
                      <div className="text-xs text-slate-500">{enrolment.email}</div>
                    </td>
                    <td className="px-4 py-3">{enrolment.courseName}</td>
                    <td className="px-4 py-3">{enrolment.campusName}</td>
                    <td className="px-4 py-3">{enrolment.scheduleDate}</td>
                    <td className="px-4 py-3"><ProgressIndicator percentage={enrolment.studentProgressPercentage} step="Student steps" /></td>
                    <td className="px-4 py-3"><ProgressIndicator percentage={enrolment.instituteProgressPercentage} step="Institute steps" /></td>
                    <td className="px-4 py-3"><div className="space-y-1 text-xs"><div>USI: {formatBoolean(enrolment.usiVerified)}</div><div>Documents: {formatBoolean(enrolment.documentsVerified)}</div></div></td>
                    <td className="px-4 py-3">{enrolment.submittedDate}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusMeta(enrolment.workFlowStatusNo).className}`}>
                        {enrolment.workFlowStatusName}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={processingId === enrolment.studentEnrollmentId}
                        onClick={() => navigate(`/admin/caurse-progress/${enrolment.studentBookingId}`, { state: { enrolment } })}
                      >
                        View Progress
                      </Button>
                    </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">{summaryText}</div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-slate-500">Rows <select className="ml-1 rounded-md border border-slate-300 px-2 py-1" value={filters.pageSize} onChange={(event) => setFilters((current) => ({ ...current, page: 1, pageSize: Number(event.target.value) }))}>{[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
            <button className="wc-btn-outline py-1.5" disabled={filters.page <= 1 || loading} onClick={() => updateFilter("page", filters.page - 1)}>Previous</button>
            <span className="text-sm text-slate-600">Page {filters.page} of {totalPages}</span>
            <button className="wc-btn-outline py-1.5" disabled={filters.page >= totalPages || loading} onClick={() => updateFilter("page", filters.page + 1)}>Next</button>
          </div>
        </div>
      </div>
      {detailLoading && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40"><div className="rounded-lg bg-white px-6 py-4 shadow-xl flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading enrolment...</div></div>}
      {detail && <EnrolmentDetailDialog detail={detail} onClose={() => setDetail(null)} onReload={() => viewEnrolment(detail.summary)} onAction={(enrolment, action, comments) => handleAction(enrolment, action, comments, () => viewEnrolment(detail.summary))} onIssueCertificate={setCertificateDialog} processingId={processingId} />}
      {historyEnrolment && <WorkflowHistoryDialog enrolment={historyEnrolment} onClose={() => setHistoryEnrolment(null)} />}
      {actionDialog && <EnrolmentActionDialog value={actionDialog} onChange={setActionDialog} onClose={() => setActionDialog(null)} onConfirm={() => handleAction(actionDialog.enrolment, actionDialog.action, actionDialog.comments)} processing={processingId === actionDialog.enrolment.studentEnrollmentId} />}
      {certificateDialog && (
        <IssueCertificateDialog
          enrolment={certificateDialog}
          onClose={() => setCertificateDialog(null)}
          onIssued={async () => {
            setCertificateDialog(null);
            await fetchEnrolments();
          }}
        />
      )}
    </div>
  );
}

function IssueCertificateDialog({ enrolment, onClose, onIssued }) {
  const [form, setForm] = useState({
    certificateName: enrolment.courseName && enrolment.courseName !== "-"
      ? `${enrolment.courseName} Certificate`
      : "Certificate",
    statementOfAttainmentNumber: "",
    dateOfAttainment: "",
    issueDate: new Date().toISOString().slice(0, 10),
    remarks: "",
    documentDescription: "Issued from Admin Enrolments",
    file: null,
  });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    if (!form.certificateName.trim()) {
      ToastHelper.error("Certificate name is required.");
      return;
    }
    if (!form.issueDate) {
      ToastHelper.error("Issue date is required.");
      return;
    }
    if (!form.file) {
      ToastHelper.error("Certificate PDF is required.");
      return;
    }
    if (form.file.type && form.file.type !== "application/pdf") {
      ToastHelper.error("Please select a PDF certificate file.");
      return;
    }
    if (form.file.size > 10 * 1024 * 1024) {
      ToastHelper.error("Certificate PDF must not exceed 10 MB.");
      return;
    }

    const data = new FormData();
    data.append("EnrollmentId", String(enrolment.studentEnrollmentId));
    data.append("CertificateTypeId", "1");
    data.append("CertificateNumber", String(enrolment.bookingNumber || enrolment.studentEnrollmentId).trim());
    data.append("CertificateName", form.certificateName.trim());
    if (form.dateOfAttainment) data.append("DateOfAttainment", new Date(`${form.dateOfAttainment}T00:00:00`).toISOString());
    data.append("IssueDate", new Date(`${form.issueDate}T00:00:00`).toISOString());
    if (form.statementOfAttainmentNumber.trim()) data.append("StatementOfAttainmentNumber", form.statementOfAttainmentNumber.trim());
    if (form.remarks.trim()) data.append("Remarks", form.remarks.trim());
    if (form.documentDescription.trim()) data.append("DocumentDescription", form.documentDescription.trim());
    data.append("File", form.file);

    setSaving(true);
    try {
      const response = await certificateService.create(data);
      ToastHelper.success(response?.message || "Certificate issued successfully.");
      window.dispatchEvent(new Event("certificates:updated"));
      await onIssued();
    } catch (error) {
      ToastHelper.apiError(error, "Unable to issue certificate.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <div className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-display font-extrabold text-[#0A2540]">Issue certificate</h2>
          <p className="mt-1 text-sm text-slate-500">{enrolment.studentName} · {enrolment.courseName} · {enrolment.bookingNumber}</p>
        </div>

        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate name *</span>
            <input className="wc-input" value={form.certificateName} onChange={(event) => set("certificateName", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Statement of Attainment number</span>
            <input className="wc-input" value={form.statementOfAttainmentNumber} onChange={(event) => set("statementOfAttainmentNumber", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Date of Attainment</span>
            <input type="date" className="wc-input" value={form.dateOfAttainment} onChange={(event) => set("dateOfAttainment", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Issue date *</span>
            <input type="date" className="wc-input" value={form.issueDate} onChange={(event) => set("issueDate", event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Certificate PDF *</span>
            <input type="file" accept="application/pdf,.pdf" className="wc-input" onChange={(event) => set("file", event.target.files?.[0] || null)} />
            <small className="text-slate-500">PDF only, maximum 10 MB.</small>
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Remarks</span>
            <textarea className="wc-input min-h-24" value={form.remarks} onChange={(event) => set("remarks", event.target.value)} />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button className="wc-btn-outline" disabled={saving} onClick={onClose}>Cancel</button>
          <button className="wc-btn-primary" disabled={saving} onClick={submit}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Issuing..." : "Issue certificate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkflowHistoryDialog({ enrolment, onClose }) {
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4"><div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b px-6 py-4"><div><h2 className="text-xl font-extrabold text-[#0A2540]">Workflow history</h2><p className="text-sm text-slate-500">{enrolment.studentName} · {enrolment.bookingNumber}</p></div><button className="text-2xl text-slate-400" onClick={onClose}>×</button></div><div className="max-h-[65vh] overflow-y-auto p-6"><EnrollmentWorkflowTimeline studentEnrollmentId={enrolment.studentEnrollmentId} loadHistory={adminEnrolmentService.getWorkflowHistory} isAdmin /></div><div className="flex justify-end border-t px-6 py-4"><button className="wc-btn-primary" onClick={onClose}>Close</button></div></div></div>;
}

function FilterSelect({ label, value, options, onChange, disabled = false }) {
  return <label><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span><select className="wc-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>{(options || []).map((option) => <option key={`${label}-${option.value}`} value={option.value}>{option.label}</option>)}</select></label>;
}

function EnrolmentActionDialog({ value, onChange, onClose, onConfirm, processing }) {
  const needsReason = true;
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4"><div className="w-full max-w-lg rounded-xl bg-white shadow-2xl"><div className="border-b px-6 py-4"><h2 className="text-xl font-extrabold text-[#0A2540]">{value.action} enrolment</h2><p className="text-sm text-slate-500">{value.enrolment.studentName} · {value.enrolment.bookingNumber}</p></div><div className="p-6"><label><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{needsReason ? "Reason / comments" : "Admin comments"}</span><textarea autoFocus className="wc-input min-h-28" value={value.comments} onChange={e=>onChange({...value,comments:e.target.value})} placeholder={needsReason ? `Enter the reason for ${value.action.toLowerCase()}ing this enrolment` : "Optional approval comments"} /></label>{needsReason && !value.comments.trim() && <p className="mt-2 text-xs text-amber-700">A reason is required.</p>}</div><div className="flex justify-end gap-2 border-t px-6 py-4"><button className="wc-btn-outline" onClick={onClose}>Cancel</button><button className="wc-btn-primary" disabled={processing || (needsReason && !value.comments.trim())} onClick={onConfirm}>{processing ? "Saving..." : `Confirm ${value.action}`}</button></div></div></div>;
}

function EnrolmentDetailDialog({ detail, onClose, onReload, onAction, onIssueCertificate, processingId }) {
  const data = detail.enrollment || {};
  const [statementNumber, setStatementNumber] = useState(data.statementOfAttainmentNumber || "");
  const [savingStatement, setSavingStatement] = useState(false);
  const [certificateForm, setCertificateForm] = useState({ certificateName: detail.summary?.courseName ? `${detail.summary.courseName} Certificate` : "Certificate", issueDate: new Date().toISOString().slice(0, 10), remarks: "", file: null });
  const [savingCertificate, setSavingCertificate] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const settingPerm = detail.settingPerm || data.settingPerm || data.SettingPerm || {};
  const showStaffCompletion = readPerm(settingPerm, "showStaffCompletion", "ShowStaffCompletion") !== false;
  const lockStaffCompletion = readPerm(settingPerm, "lockStaffCompletion", "LockStaffCompletion") !== false;
  const [staffForm, setStaffForm] = useState({
    staffEvidenceSubmitted: boolOrNull(data.staffEvidenceSubmitted ?? data.StaffEvidenceSubmitted),
    staffEntryRequirementsSatisfied: boolOrNull(data.staffEntryRequirementsSatisfied ?? data.StaffEntryRequirementsSatisfied),
    staffOutcomeNotified: boolOrNull(data.staffOutcomeNotified ?? data.StaffOutcomeNotified),
    staffName: data.staffName ?? data.StaffName ?? "",
    staffSignatureText: data.staffSignatureText ?? data.StaffSignatureText ?? "",
    staffSignatureDate: data.staffSignatureDate ?? data.StaffSignatureDate ?? "",
  });
  const [savingStaff, setSavingStaff] = useState(false);
  const [detailAction, setDetailAction] = useState(null);
  const workFlowStatusNo = Number(data.workFlowStatusNo ?? detail.summary?.workFlowStatusNo);
  const canEditStatement = [EnrollmentStatus.Completed, EnrollmentStatus.CertificateIssued].includes(workFlowStatusNo);
  const canIssueCertificate = workFlowStatusNo === EnrollmentStatus.Completed;
  const canDownloadEnrolment = canDownloadEnrollmentDocument(workFlowStatusNo);
  const downloadEnrolmentDocx = async () => {
    try {
      await enrollmentDocumentService.downloadAdminEnrollment(data.studentBookingId ?? detail.summary?.studentBookingId);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to download enrolment document.");
    }
  };
  const saveStatementNumber = async () => {
    setSavingStatement(true);
    try {
      const response = await adminEnrolmentService.updateStatementOfAttainmentNumber(
        data.studentEnrollmentId,
        statementNumber.trim() || null,
      );
      data.statementOfAttainmentNumber = response.statementOfAttainmentNumber || "";
      setStatementNumber(data.statementOfAttainmentNumber);
      ToastHelper.success(response.message || "Statement of Attainment Number saved.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save Statement of Attainment Number.");
    } finally {
      setSavingStatement(false);
    }
  };
  const issueCertificate = async () => {
    if (!certificateForm.certificateName.trim() || !certificateForm.issueDate || !certificateForm.file) {
      ToastHelper.error("Certificate name, issue date and PDF are required.");
      return;
    }
    const formData = new FormData();
    formData.append("EnrollmentId", String(data.studentEnrollmentId ?? detail.summary?.studentEnrollmentId));
    formData.append("CertificateTypeId", "1");
    formData.append("CertificateNumber", String(detail.summary?.bookingNumber || data.studentEnrollmentId || detail.summary?.studentEnrollmentId).trim());
    formData.append("CertificateName", certificateForm.certificateName.trim());
    formData.append("IssueDate", new Date(`${certificateForm.issueDate}T00:00:00`).toISOString());
    if (statementNumber.trim()) formData.append("StatementOfAttainmentNumber", statementNumber.trim());
    if (certificateForm.remarks.trim()) formData.append("Remarks", certificateForm.remarks.trim());
    formData.append("File", certificateForm.file);
    setSavingCertificate(true);
    try {
      const response = await certificateService.create(formData);
      data.workFlowStatusNo = EnrollmentStatus.CertificateIssued;
      detail.summary.workFlowStatusNo = EnrollmentStatus.CertificateIssued;
      ToastHelper.success(response?.message || "Certificate issued successfully.");
      window.dispatchEvent(new Event("certificates:updated"));
    } catch (error) {
      ToastHelper.apiError(error, "Unable to issue certificate.");
    } finally {
      setSavingCertificate(false);
    }
  };
  const updateCertificateField = (key, value) => setCertificateForm((current) => ({ ...current, [key]: value }));
  const display = (value) => {
    if (value === true) return "Yes"; if (value === false) return "No";
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const date = new Date(value);
      return isEmptyDate(date) ? "-" : date.toLocaleDateString("en-AU");
    }
    if (Array.isArray(value)) return value.map(x => typeof x === "object" ? JSON.stringify(x) : x).join(", ") || "-";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };
  const studentEnrollmentId =
    data.studentEnrollmentId ?? data.StudentEnrollmentId ?? detail.summary?.studentEnrollmentId;
  const buttonList = Array.isArray(detail.buttonList) ? detail.buttonList : [];
  const setStaff = (key, value) => setStaffForm((current) => ({ ...current, [key]: value }));
  const saveStaffCompletion = async () => {
    if (lockStaffCompletion) return;
    setSavingStaff(true);
    try {
      const enrollment = {
        ...data,
        staffEvidenceSubmitted: staffForm.staffEvidenceSubmitted,
        staffEntryRequirementsSatisfied: staffForm.staffEntryRequirementsSatisfied,
        staffOutcomeNotified: staffForm.staffOutcomeNotified,
        staffName: staffForm.staffName || null,
        staffSignatureText: staffForm.staffSignatureText || null,
        staffSignatureDate: staffForm.staffSignatureDate || null,
      };
      await adminEnrolmentService.saveStaffCompletion({
        enrollment,
        oldEnrollment: data,
      });
      ToastHelper.success("Staff Completion saved.");
      if (typeof onReload === "function") await onReload();
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save Staff Completion.");
    } finally {
      setSavingStaff(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-slate-50 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-display font-extrabold text-[#0A2540]">Enrolment details</h2>
            <p className="text-sm text-slate-500">{detail.summary.studentName} · {detail.summary.courseName} · {detail.summary.bookingNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            {canDownloadEnrolment && <button type="button" className="wc-btn-primary text-sm" onClick={downloadEnrolmentDocx}><Download className="h-4 w-4" /> Download Enrolment</button>}
            <button className="text-2xl text-slate-400" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="p-6">
          <OnlineEnrolmentNew
            key={`${data.studentBookingId ?? detail.summary?.studentBookingId}-${workFlowStatusNo}-${formVersion}`}
            studentBookingId={data.studentBookingId ?? detail.summary?.studentBookingId}
            isAdminReview
            workflowButtonList={buttonList}
            onWorkflowButton={(button, options = {}) => {
              const action = normalizeAction(button);
              if (getButtonNextStatus(button) === EnrollmentStatus.CertificateIssued) {
                if (typeof onIssueCertificate === "function") onIssueCertificate(detail.summary);
                return;
              }
              setDetailAction({
                  enrolment: {
                    ...data,
                    ...detail.summary,
                    studentEnrollmentId,
                    studentBookingId: data.studentBookingId ?? detail.summary?.studentBookingId,
                    studentName: detail.summary?.studentName || [data.firstName, data.lastName].filter(Boolean).join(" ") || "Student",
                    bookingNumber: detail.summary?.bookingNumber,
                  },
                  button,
                  action,
                  comments: "",
                  beforeAction: options.beforeAction || null,
                });
            }}
          />
        </div>

        <div className="sticky bottom-0 flex justify-end border-t bg-white px-6 py-4">
          <button className="wc-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
      {detailAction && <EnrolmentActionDialog value={detailAction} onChange={setDetailAction} onClose={() => setDetailAction(null)} onConfirm={async () => { if (typeof detailAction.beforeAction === "function") { const result = await detailAction.beforeAction(); if (!result?.success) return; } await onAction(detailAction.enrolment, detailAction.button, detailAction.comments); setFormVersion((value) => value + 1); setDetailAction(null); }} processing={processingId === studentEnrollmentId} />}
    </div>
  );
}
